<template>
    <div style="max-width: 600px; margin: auto; text-align: left;">
        <FieldHeader class="q-mb-xs" v-if="showHeader" :field="field" />
        <div v-for="(item, index) in items" :key="`multi-item-${index}`" class="q-mb-md">
            <div class="row items-start q-col-gutter-sm">
                <div class="col">
                    <template v-if="isInline">
                        <div class="row items-start no-wrap multi-field-inline-item">
                            <div class="text-caption text-grey-7 q-mr-sm multi-field-inline-label">
                                {{ inlineItemLabel(index) }}
                            </div>
                            <div class="col">
                                <component
                                    :is="resolvedNestedComponent"
                                    v-if="resolvedNestedComponent"
                                    :field="nestedField"
                                    :model-value="item"
                                    :read-only="readOnly"
                                    :options="nestedSelectOptions"
                                    :rules="nestedDateValidationRules"
                                    :date-options="nestedDatePickerOptions"
                                    :show-header="false"
                                    @update:model-value="onItemUpdate(index, $event)"
                                    @blur="$emit('blur')"
                                />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="text-caption text-grey-7 q-mb-xs">
                            {{ itemLabel(index) }}
                        </div>
                        <component
                            :is="resolvedNestedComponent"
                            v-if="resolvedNestedComponent"
                            :field="nestedField"
                            :model-value="item"
                            :read-only="readOnly"
                            :options="nestedSelectOptions"
                            :rules="nestedDateValidationRules"
                            :date-options="nestedDatePickerOptions"
                            :show-header="false"
                            @update:model-value="onItemUpdate(index, $event)"
                            @blur="$emit('blur')"
                        />
                    </template>
                </div>
                <div v-if="!readOnly" class="col-auto">
                    <q-btn
                        :style="items.length <= minCount ? 'visibility: hidden' : ''"
                        flat
                        round
                        dense
                        color="negative"
                        icon="delete"
                        :disable="items.length <= minCount"
                        @click="removeItem(index)"
                    />
                </div>
            </div>
        </div>

        <div v-if="!readOnly && canAdd" class="text-left">
            <q-btn
                outline
                no-caps
                color="primary"
                label="Add another"
                @click="addItem"
            />
        </div>
    </div>
</template>

<script>
    import FieldHeader from './FieldHeader.vue';
    import CellsField from './CellsField.vue';
    import DateField from './DateField.vue';
    import RadioField from './RadioField.vue';
    import ScaleField from './ScaleField.vue';
    import SelectField from './SelectField.vue';
    import StarRatingField from './StarRatingField.vue';
    import TextField from './TextField.vue';
    import selectOptionsMixin from '../mixins/selectOptions.mixin';
    import fieldTypesAndDateMixin from '../mixins/fieldTypesAndDate.mixin';

    export default {
        name: 'MultiField',
        mixins: [selectOptionsMixin, fieldTypesAndDateMixin],
        components: {
            FieldHeader,
            CellsField,
            DateField,
            RadioField,
            ScaleField,
            SelectField,
            StarRatingField,
            TextField,
        },
        props: {
            field: {
                type: Object,
                required: true,
            },
            modelValue: {
                type: [Array, String],
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['update:modelValue', 'blur'],
        computed: {
            minCount() {
                const value = Number(this.field?.options?.min);
                return Number.isInteger(value) && value >= 0 ? value : 0;
            },
            maxCount() {
                const value = Number(this.field?.options?.max);
                return Number.isInteger(value) && value > 0 ? value : null;
            },
            nestedType() {
                return this.field?.options?.field?.type || 'TEXT';
            },
            nestedField() {
                return {
                    id: `${this.field?.id || 'multi'}-nested`,
                    fieldType: this.nestedType,
                    required: false,
                    options: {
                        ...(this.field?.options?.field?.options || {}),
                    },
                };
            },
            resolvedNestedComponent() {
                return this.resolveFieldComponentByType(this.nestedType);
            },
            normalizedModelValue() {
                if (Array.isArray(this.modelValue)) {
                    return [...this.modelValue];
                }
                return [];
            },
            items() {
                const values = this.normalizedModelValue;
                if (values.length >= this.minCount) {
                    return values;
                }
                const placeholders = Array.from({ length: this.minCount - values.length }, () => this.defaultItemValue());
                return [...values, ...placeholders];
            },
            canAdd() {
                if (this.maxCount === null) return true;
                return this.items.length < this.maxCount;
            },
            nestedSelectOptions() {
                return this.buildSelectOptions(this.nestedField.options?.options || []);
            },
            nestedDateValidationRules() {
                return this.buildDateValidationRules(this.nestedField.options || {});
            },
            isInline() {
                return Boolean(this.field?.options?.inline);
            },
        },
        methods: {
            defaultItemValue() {
                if (this.nestedType === 'STAR_RATING') return 0;
                if (this.nestedType === 'SCALE') return null;
                if (this.nestedType === 'CELLS') {
                    const fieldDefinitions = this.nestedField?.options?.fields || this.nestedField?.options?.values || [];
                    const initialValue = {};

                    fieldDefinitions.forEach((fieldDefinition, index) => {
                        if (typeof fieldDefinition === 'string') {
                            initialValue[fieldDefinition] = '';
                            return;
                        }

                        if (fieldDefinition && typeof fieldDefinition === 'object' && !Array.isArray(fieldDefinition)) {
                            const [key] = Object.entries(fieldDefinition)[0] || [`field_${index + 1}`];
                            initialValue[key] = '';
                        }
                    });

                    return initialValue;
                }
                return '';
            },
            itemLabel(index) {
                const itemNumber = index + 1;
                const ordinal = this.toOrdinal(itemNumber);
                const configuredLabel = this.field?.options?.field?.name || this.field?.options?.itemLabel || 'item';

                return `${ordinal} ${configuredLabel}`;
            },
            inlineItemLabel(index) {
                const itemNumber = index + 1;
                return `${itemNumber})`;
            },
            toOrdinal(value) {
                const normalizedValue = Number(value);

                if (!Number.isInteger(normalizedValue) || normalizedValue <= 0) return `${value}`;

                const remainder100 = normalizedValue % 100;
                if (remainder100 >= 11 && remainder100 <= 13) {
                    return `${normalizedValue}th`;
                }

                const remainder10 = normalizedValue % 10;
                if (remainder10 === 1) return `${normalizedValue}st`;
                if (remainder10 === 2) return `${normalizedValue}nd`;
                if (remainder10 === 3) return `${normalizedValue}rd`;

                return `${normalizedValue}th`;
            },
            emitItems(nextItems) {
                this.$emit('update:modelValue', nextItems.length ? nextItems : '');
            },
            onItemUpdate(index, value) {
                const nextItems = [...this.items];
                nextItems[index] = value;
                this.emitItems(nextItems);
            },
            addItem() {
                if (!this.canAdd) return;
                const nextItems = [...this.items, this.defaultItemValue()];
                this.emitItems(nextItems);
                this.$emit('blur');
            },
            removeItem(index) {
                if (this.items.length <= this.minCount) return;
                const nextItems = this.items.filter((_, itemIndex) => itemIndex !== index);
                this.emitItems(nextItems);
                this.$emit('blur');
            },
            nestedDatePickerOptions(date) {
                return this.buildDatePickerOptions(this.nestedField.options || {})(date);
            },
        },
    };
</script>

<style scoped>
    .multi-field-inline-item {
        align-items: center;
    }

    .multi-field-inline-label {
        display: flex;
        align-items: center;
    }
</style>
